import { Field, Form, Formik } from "formik";
import { useGetAccounts } from "hooks/useAccounts";
import { isEmpty } from "lodash";
import Button from "pages/_components/Button";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Selector from "pages/_components/fields/formik/Selector";
import GridTable from "pages/_components/GridTable/GridTable";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as i18n from "util/i18n";
import { amountTypes, formatPreparedData } from "util/servicePayments";
import { goBack } from "connected-react-router";
import { DynamicForm, MultipleBill, SingleBill } from "./components";
import { useBilling, useCompanies, useDynamicForm, usePaymentModes, useValidations } from "./hooks";

const FORM_ID = "payments.service";
const initialValues = {
    companyCode: "",
    paymentType: "",
    debitAccountId: "",
    amountField: {
        amount: null,
        currency: "ARS",
    },
};

const NewServicePaymentPage = ({ isDesktop }) => {
    const [billToPay, setBillToPay] = useState(null);
    const [multiAmountList, setMultiAmountList] = useState([]);
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const placeholder = i18n.get("global.select");

    const { accountOptions, fetching, accounts, formatLabel } = useGetAccounts("ARS", "", "", true);
    const { forms, handleDynamicValues, dynamicSchema } = useDynamicForm();
    const { companyName, companies, fetchingCompanies, handleCompanyInputChange, handleCompanyChange } = useCompanies();
    const {
        paymentModes,
        fetchingModes,
        hasPaymentType,
        handleModesChange,
        paymentTypeSelected,
        isCABpayment,
    } = usePaymentModes(handleDynamicValues);
    const { handleInvoices, billingList, fetchingBilling, fetchingPrepare, hasBilling } = useBilling();
    const { validationSchema } = useValidations(accounts, billToPay, hasBilling, dynamicSchema, isCABpayment);

    useEffect(() => {
        const multiAmount = billToPay?.details
            ?.filter(
                (detail) =>
                    // eslint-disable-next-line no-restricted-globals
                    !isNaN(parseFloat(detail.value)) && isFinite(detail.value) && Number(detail.value) % 1 !== 0,
            )
            ?.map((bill) => ({
                value: bill.label,
                label: `${bill.label} - $ ${bill.value}`,
                amount: Number(bill.value),
            }));

        formRef.current.setFieldValue("multiAmountSelect", "");

        formRef.current.setFieldValue("amountField", {
            amount: 0,
            currency: "ARS",
        });

        setMultiAmountList(multiAmount);
    }, [billToPay]);

    const handleMultiAmountChange = (id) => {
        const amountSelected = multiAmountList.filter((item) => item.value === id);

        formRef.current.setFieldValue("amountField", {
            amount: amountSelected[0].amount,
            currency: "ARS",
        });
    };

    const handleSubmit = (values) => {
        const accountLabel = formatLabel(
            accounts.find(({ productId }) => productId === values.debitAccountId),
            true,
        );

        const form = Object.fromEntries(
            Object.entries(values).filter(
                ([key]) =>
                    !key.includes("companyCode") &&
                    !key.includes("paymentType") &&
                    !key.includes("debitAccountId") &&
                    !key.includes("amountField") &&
                    !key.includes("multiAmountSelect"),
            ),
        );

        const params = {
            form,
            billToPay,
            values,
            accountLabel,
            isCABpayment,
            companyName,
        };

        const { prepareData } = formatPreparedData(params);

        gaEventTracker.triggerEvent(events.pagos_realizar_nuevo_pago());
        dispatch(servicePaymentsActions.preparePayment(prepareData));
    };

    useEffect(() => {
        dispatch(servicePaymentsActions.resetPaymentForm());
    }, []);

    useEffect(() => {
        if (billingList?.length === 1) {
            const _billToPay = billingList?.filter((bill) => !bill.expired)[0];
            setBillToPay(_billToPay);
        }
    }, [billingList]);

    useEffect(() => {
        if (!formRef.current || billingList?.length === 0) {
            return;
        }
        formRef.current.setFieldValue("amountField", {
            amount: billToPay?.amount1,
            currency: "ARS",
        });
    }, [billToPay]);

    const handleBack = () => {
        dispatch(servicePaymentsActions.resetPaymentForm());
        dispatch(goBack());
    };

    return (
        <>
            <Notification scopeToShow="newServicePaymentPage" />
            <ResponsiveHeader id="payments.service.new" onClickBack={handleBack} />
            <MainContainer showLoader={fetching}>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    ref={formRef}>
                    {({ isValid, dirty, values, resetForm }) => {
                        const selectedAccount = accounts.find(({ productId }) => productId === values.debitAccountId);
                        const hasBalace = selectedAccount?.availableBalance > values?.amountField?.amount;
                        const canSubmit =
                            !values.debitAccountId ||
                            !values.amountField?.amount ||
                            isEmpty(billToPay) ||
                            values.amountField?.amount < billToPay?.minAmount1 ||
                            values.amountField?.amount > billToPay?.maxAmount1 ||
                            !hasBalace;
                        const isClosedAmount = billToPay?.amountType === amountTypes.ESC;
                        const disableAmountField = !values.debitAccountId || isClosedAmount;

                        return (
                            <Form>
                                <GridTable className="form service-payments-with-search">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data columnStart={2} columnWidth={10}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="companyCode"
                                                options={companies}
                                                onInputChange={handleCompanyInputChange}
                                                onChange={(id) => handleCompanyChange(id, resetForm, initialValues)}
                                                placeholder={i18n.get("servicePayments.search")}
                                                isLoading={fetchingCompanies}
                                                disabled={fetchingModes}
                                                searchable
                                                clearable
                                                maxLength={40}
                                            />
                                        </GridTable.Data>
                                    </GridTable.Container>
                                    {hasPaymentType && (
                                        <>
                                            <GridTable.Container columns={12} columnsTemplate="fixed">
                                                <GridTable.Data columnStart={2} columnWidth={10}>
                                                    <Field
                                                        component={Selector}
                                                        idForm={FORM_ID}
                                                        name="paymentType"
                                                        options={paymentModes}
                                                        onChange={(id) => handleModesChange(id, values, resetForm)}
                                                        placeholder={i18n.get("global.select")}
                                                        isLoading={fetchingModes}
                                                        disabled={fetchingModes || hasBilling}
                                                    />
                                                </GridTable.Data>
                                            </GridTable.Container>
                                            <DynamicForm
                                                forms={forms}
                                                idForm={FORM_ID}
                                                disabled={hasBilling}
                                                docType={values?.companyCode === "1256" ? values?.DAD : ""}
                                            />
                                        </>
                                    )}
                                </GridTable>
                                {isCABpayment && (
                                    <>
                                        <GridTable.Data columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                label={`${i18n.get(`${FORM_ID}.debitAccountId.label`)}*`}
                                                name="debitAccountId"
                                                placeholder={placeholder}
                                                data={{ emptyOptionLabel: "", options: accountOptions }}
                                            />
                                        </GridTable.Data>
                                        <Row className="form-actions">
                                            <Col xs={12} md={4} mdOffset={4}>
                                                <Button
                                                    type="submit"
                                                    className="btn-primary"
                                                    label="payments.service.pay"
                                                    loading={fetchingPrepare}
                                                    disabled={!values.debitAccountId}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                {hasBilling && (
                                    <>
                                        <div style={!isDesktop ? { marginTop: "-25px", marginBottom: "-25px" } : null}>
                                            <GridTable className="form my-6 py-6">
                                                <GridTable.Container
                                                    columns={12}
                                                    columnsTemplate="fixed"
                                                    className={
                                                        !isDesktop && billingList.length <= 1 && "box box-shadow"
                                                    }>
                                                    <GridTable.Data columnStart={2} columnWidth={10}>
                                                        {billingList.length > 1 ? (
                                                            <MultipleBill
                                                                billingList={billingList}
                                                                isDesktop={isDesktop}
                                                                paymentType={paymentTypeSelected}
                                                                billToPay={billToPay}
                                                                setBillToPay={setBillToPay}
                                                                className="data-amount-wrapper"
                                                            />
                                                        ) : (
                                                            <SingleBill
                                                                bill={billingList[0]}
                                                                paymentType={paymentTypeSelected}
                                                            />
                                                        )}
                                                    </GridTable.Data>
                                                </GridTable.Container>
                                            </GridTable>
                                        </div>
                                        <GridTable className="form my-6 py-6">
                                            <GridTable.Container columns={12} columnsTemplate="fixed">
                                                {!isEmpty(billToPay) && (
                                                    <GridTable.Data columnStart={2} columnWidth={10}>
                                                        <Field
                                                            component={ProductSelector}
                                                            label={`${i18n.get(`${FORM_ID}.debitAccountId.label`)}*`}
                                                            name="debitAccountId"
                                                            placeholder={placeholder}
                                                            data={{ emptyOptionLabel: "", options: accountOptions }}
                                                        />
                                                    </GridTable.Data>
                                                )}

                                                {/* Manejo de casos cuando factura tienen 2 importes un min y un max */}
                                                {!isEmpty(billToPay) && multiAmountList?.length >= 1 && (
                                                    <GridTable.Data columnStart={2} columnWidth={6}>
                                                        <Field
                                                            component={Selector}
                                                            idForm={FORM_ID}
                                                            name="multiAmountSelect"
                                                            label={`${i18n.get("payments.service.totalAmount")}*`}
                                                            options={multiAmountList}
                                                            onChange={(id) => handleMultiAmountChange(id)}
                                                            placeholder={i18n.get("global.select")}
                                                            disabled={false}
                                                        />
                                                    </GridTable.Data>
                                                )}
                                                {!isEmpty(billToPay) && multiAmountList?.length <= 0 && (
                                                    <GridTable.Data columnStart={2} columnWidth={5}>
                                                        <Field
                                                            labelText={`${i18n.get("payments.service.totalAmount")}*`}
                                                            component={AmountField}
                                                            idForm={FORM_ID}
                                                            name="amountField"
                                                            data={{
                                                                options: [
                                                                    { id: 1, label: i18n.get(`currency.label.ARS`) },
                                                                ],
                                                            }}
                                                            disableNumber={disableAmountField}
                                                            clearable={false}
                                                            maxLength={16}
                                                            disable={isClosedAmount}
                                                            disableSelect
                                                            fixedDecimalScale
                                                        />
                                                    </GridTable.Data>
                                                )}
                                            </GridTable.Container>
                                        </GridTable>
                                        <Row className="form-actions">
                                            <Col xs={12} md={4} mdOffset={4}>
                                                <Button
                                                    type="submit"
                                                    className="btn-primary"
                                                    label="payments.service.pay"
                                                    loading={fetchingPrepare}
                                                    disabled={canSubmit}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                {!hasBilling && !isCABpayment && (
                                    <Row className="form-actions">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                className="btn-primary"
                                                label="global.continue"
                                                onClick={() => handleInvoices(values)}
                                                disabled={!(isValid && dirty)}
                                                loading={fetchingBilling}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

NewServicePaymentPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default NewServicePaymentPage;
